/** Room types (Fallout-Shelter slots). Simplified loop: Grow → Deal on a single
 *  `product` pool; Lab & Packaging are OPTIONAL income boosters, not stages. */
import type { RoomType, ResourceKey, NeedKey, SkillKey } from '../types';

export interface RoomDef {
  type: RoomType;
  name: string;
  icon: string;
  accent: string;
  glow: string;
  blurb: string;
  cost: number;
  workSlots: number; // capacity (workers; or sleepers/eaters/relaxers for services)
  /** Assignable workplace (vs. a passive service like bunks/rec/stash). */
  workplace?: boolean;
  /** Skill that scales the work rate (omit = flat rate). */
  skill?: SkillKey;

  // production (per sim-second at skill ~5, tier 1)
  consume?: ResourceKey;
  produce?: ResourceKey;
  rate?: number;
  sellPrice?: number; // deal: cash per unit sold
  heatPerUnit?: number;
  cashPerUnit?: number; // diner: cash per meal cooked

  // optional booster effect (applied when staffed)
  boost?: 'value' | 'throughput';

  // life support
  satisfies?: NeedKey;

  // ops
  heatReduce?: number;
  defense?: number;
  stash?: boolean;
}

export const ROOM_DEFS: Record<RoomType, RoomDef> = {
  grow: {
    type: 'grow',
    name: 'Grow Room',
    icon: '🌿',
    accent: '#3fae54',
    glow: 'rgba(63,174,84,0.45)',
    blurb: 'Hydroponic trays under grow-lights. Botanists grow the product.',
    cost: 120,
    workSlots: 2,
    workplace: true,
    skill: 'growing',
    produce: 'product',
    rate: 0.6,
    heatPerUnit: 0.05,
  },
  deal: {
    type: 'deal',
    name: 'Deal Spot',
    icon: '🤝',
    accent: '#ff5470',
    glow: 'rgba(255,84,112,0.45)',
    blurb: 'The drop. Dealers move product to the street for cash.',
    cost: 130,
    workSlots: 2,
    workplace: true,
    skill: 'dealing',
    consume: 'product',
    produce: 'cash',
    rate: 0.5,
    sellPrice: 30,
    heatPerUnit: 0.9,
  },
  lab: {
    type: 'lab',
    name: 'Cut Lab',
    icon: '⚗️',
    accent: '#9d6bff',
    glow: 'rgba(157,107,255,0.45)',
    blurb: 'Optional. Staff it to cut & refine — product sells for +75% more.',
    cost: 200,
    workSlots: 1,
    workplace: true,
    boost: 'value',
  },
  pack: {
    type: 'pack',
    name: 'Packaging',
    icon: '📦',
    accent: '#ffb454',
    glow: 'rgba(255,180,84,0.45)',
    blurb: 'Optional. Staff it to bag in bulk — grow rooms yield +65%.',
    cost: 180,
    workSlots: 1,
    workplace: true,
    boost: 'throughput',
  },
  bunk: {
    type: 'bunk',
    name: 'Bunks',
    icon: '🛏️',
    accent: '#4ea8ff',
    glow: 'rgba(78,168,255,0.4)',
    blurb: 'Beds. The crew sleeps here to recover Rest.',
    cost: 90,
    workSlots: 3,
    satisfies: 'rest',
  },
  diner: {
    type: 'diner',
    name: 'Diner',
    icon: '🍳',
    accent: '#ffd54a',
    glow: 'rgba(255,213,74,0.4)',
    blurb: 'The crew eats here to restore Food. Groceries restock on their own; a cook makes extra.',
    cost: 110,
    workSlots: 1,
    workplace: true,
    produce: 'meals',
    rate: 0.6,
    cashPerUnit: 3,
    satisfies: 'food',
  },
  rec: {
    type: 'rec',
    name: 'Rec Room',
    icon: '🎮',
    accent: '#ff79c6',
    glow: 'rgba(255,121,198,0.4)',
    blurb: 'Couch, TV, arcade. The crew unwinds here to restore Joy.',
    cost: 100,
    workSlots: 3,
    satisfies: 'joy',
  },
  security: {
    type: 'security',
    name: 'Lookout',
    icon: '👁️',
    accent: '#2ed3c6',
    glow: 'rgba(46,211,198,0.4)',
    blurb: 'A lookout on combat duty. Cuts HEAT and defends raids.',
    cost: 180,
    workSlots: 1,
    workplace: true,
    skill: 'combat',
    heatReduce: 0.5,
    defense: 7,
  },
  stash: {
    type: 'stash',
    name: 'Stash',
    icon: '🗄️',
    accent: '#9aa0b4',
    glow: 'rgba(154,160,180,0.35)',
    blurb: 'A hidden room. Protects a chunk of product & cash from raids.',
    cost: 160,
    workSlots: 0,
    stash: true,
  },
};

export const BUILDABLE: RoomType[] = ['grow', 'deal', 'bunk', 'diner', 'rec', 'lab', 'pack', 'security', 'stash'];

export function roomDef(type: RoomType): RoomDef {
  return ROOM_DEFS[type];
}
